import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTour } from "@/contexts/TourContext";
import { Sparkles, X } from "lucide-react";

export function TourPrompt() {
  const { startTour } = useTour();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [cookieResolved, setCookieResolved] = useState<boolean>(
    () => typeof window !== "undefined" && !!localStorage.getItem("sendfeedback_cookie_consent")
  );

  useEffect(() => {
    // Wait until the user has resolved the cookie banner — both anchor bottom-right
    // and stacking them blocks interaction with either one.
    if (!cookieResolved) {
      const onConsent = () => setCookieResolved(true);
      window.addEventListener("cookie-consent-updated", onConsent);
      return () => window.removeEventListener("cookie-consent-updated", onConsent);
    }

    // Check if tour was already completed or prompt was dismissed
    const tourCompleted = localStorage.getItem("tourCompleted");
    const promptDismissed = localStorage.getItem("tourPromptDismissed");

    if (!tourCompleted && !promptDismissed) {
      // Longer delay (1s) to let the dashboard fully load after onboarding
      const timer = setTimeout(() => {
        setShowPrompt(true);
        // Trigger animation after mount
        requestAnimationFrame(() => setIsVisible(true));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cookieResolved]);

  const handleStartTour = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShowPrompt(false);
      startTour();
    }, 200);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem("tourPromptDismissed", "true");
    setTimeout(() => setShowPrompt(false), 200);
  };

  if (!showPrompt) return null;

  return (
    <div
      className={`fixed bottom-20 right-4 z-50 md:bottom-6 md:right-6 transition-all duration-200 ${
        isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95"
      }`}
    >
      <Card className="w-[300px] shadow-lg border-primary/20">
        <CardContent className="p-4 relative">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
            aria-label="Dismiss tour prompt"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground text-sm">
                Welcome to Sendfeedback.ai!
              </h3>
              <p className="text-muted-foreground text-xs mt-1">
                Take a tour of the features?
              </p>
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleStartTour}
              size="sm"
              className="flex-1 min-h-[44px]"
            >
              Start Tour
            </Button>
            <Button
              onClick={handleDismiss}
              variant="outline"
              size="sm"
              className="flex-1 min-h-[44px]"
            >
              Maybe Later
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
